import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, setTokens } from "../api.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Button } from "../components/ui/button.js";

export default function LoginPage({ onLogin, onRegister }: { onLogin: () => void; onRegister: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError("Por favor completa tu correo y contraseña");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api<{ token: string; refreshToken: string }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      setTokens(res.token, res.refreshToken);
      onLogin();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al iniciar sesión";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="FolioListo" className="h-24 w-24" />
            <div>
              <CardTitle className="text-3xl font-heading">Folio<span className="text-primary">Listo</span></CardTitle>
              <CardDescription>Emisión ágil de boletas y honorarios ⚡</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/25 text-destructive text-sm font-medium flex items-start gap-2">
                <span className="shrink-0 mt-0.5">⚠️</span>
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                className={`transition-colors ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                className={`transition-colors ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
                required
              />
            </div>
            <Button type="submit" variant="cta" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ingresando...
                </>
              ) : (
                "Ingresar"
              )}
            </Button>
            <button
              type="button"
              onClick={onRegister}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ¿No tienes cuenta? Regístrate
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
